import type { FC } from "react"
import { useCallback } from "react"
import { useQuery } from "@rocicorp/zero/react"

import { getHostname } from "@productlane/lib/src/browser"
import { Alert, Avatar, Empty } from "@productlane/ui"
import { queries } from "@productlane/zero/queries"

import EmptyCompany from "@/assets/empty-states/empty-company.png"
import {
	DetailScreenEditor,
	DetailScreenLayout,
} from "@/components/detail-screen"
import { MetaInfo } from "@/components/meta-info"
import { useShortcut } from "@/lib"
import { api } from "@/lib/api"
import { useBackNavigation, useSetBackNavigation } from "@/lib/back-navigation"
import { useZeroWorkspace } from "@/lib/zero"
import { SupportPortalSettingsContent } from "@/screens/settings/support-portal-settings-content"
import { DetailBreadcrumb, DetailTopBar } from "../page-header"
import { PublishPortalPageButton } from "../portal/publish-portal-page-button"
import { PortalRequestsPreview } from "./portal-requests-preview"

export const ContactPortalPreviewScreen: FC<{ contactId: string }> = ({
	contactId,
}) => {
	const workspace = useZeroWorkspace()
	const [contact] = useQuery(queries.contactById(contactId))
	useSetBackNavigation({
		type: "contacts-detail",
		url: `/support-portal/contact/${contactId}`,
	})

	const { goBack } = useBackNavigation()
	const close = useCallback(() => {
		goBack("/support-portal")
	}, [goBack])
	useShortcut("esc", close)

	const { data, isLoading } = api.portal.getContactPortalRequests.useQuery({
		contactId,
	})

	if (!contact) {
		return (
			<div className="flex min-h-0 w-full flex-1 items-center justify-center">
				<Empty
					text="The contact might have been removed or you no longer have access."
					title="Contact not found"
					className="max-w-[20rem]"
					variant="center"
					image={<img src={EmptyCompany} alt="Contact not found" />}
				/>
			</div>
		)
	}

	return (
		<>
			<MetaInfo title="Support portal" />
			<DetailTopBar className="justify-between">
				<div className="flex min-w-0 items-center gap-1">
					<DetailBreadcrumb
						parentLabel="Support portal"
						onParentClick={close}
						currentIcon={
							<Avatar
								size="xs"
								name={contact.name || contact.email}
								image={contact.imageUrl}
							/>
						}
						currentLabel={contact.name || contact.email}
					/>
				</div>
				{workspace && (
					<PublishPortalPageButton
						content={<SupportPortalSettingsContent />}
						isPublished={workspace.customerPortalSettings?.isEnabled || false}
						url={`https://${workspace.domain === "hello" ? "productlane.com" : getHostname(workspace)}/support`}
						name="support-portal"
					/>
				)}
			</DetailTopBar>
			<DetailScreenLayout>
				<DetailScreenEditor isPadded={false}>
					<div className="px-6 py-10 pb-6">
						<h1 className="text-2xl font-semibold text-primary">
							Support portal
						</h1>
						<p className="mt-1.5 text-sm text-secondary">
							Your support messages and feature requests.
						</p>
					</div>

					<div className="mx-6">
						<Alert variant="info">
							This is how the support portal looks to{" "}
							<span className="font-semibold">{contact.email}</span> when they
							sign in.
						</Alert>
					</div>

					<div className="mt-4 px-6">
						<PortalRequestsPreview
							requests={data?.requests ?? []}
							isLoading={isLoading}
							isDueDateEnabled={workspace?.isDueDateEnabled ?? false}
							userEmail={contact.email}
							emptyText="When this contact submits requests or upvotes items, they appear here."
						/>
					</div>
				</DetailScreenEditor>
			</DetailScreenLayout>
		</>
	)
}
